import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";

import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { AccountAdviceType } from "../../../../../../../Wrap/Constants/Enums/Apply/AccountAdviceType";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { AssetClassType } from "../../../../../../../Wrap/Constants/Enums/Domain/Instrument/AssetClassType";
import { ModelPortfolioTabType } from "../../../../../../../Wrap/Constants/Enums/Domain/ModelPortfolioTabType";
import { OrderType } from "../../../../../../../Wrap/Constants/Enums/Domain/OrderType";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { Instrument } from "../../../../../../../Wrap/Models/Domains/Investment/Instrument";
import { InstrumentSearchQuery } from "../../../../../../../Wrap/Models/Domains/Investment/InstrumentSearchQuery";
import { ClientTradableInvestments } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/ClientTradableInvestments";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { InvestmentSelectionAction } from "../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { DecisionMaker } from "../../../../../../Shared/IST/Components/DecisionMaker";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Action } from "../../../../../Actions/Action";
import { ClientAccountStore, UserStore, TreasuredAssetsStore } from "../../../../../Stroes/Variable";
import { BuyAction } from "../../../Buy/Action/BuyAction";
import { InvestmentsAction } from "../../../PortfolioDashboard/Components/Investment/Action/InvestmentAction";
import { BuySellAllocationTable } from "../../../Common/BuySellInvestmentSelection/BuySellAllocationTable";
import { RestrictionStore } from "../../../Common/Restrictions/Variable";
import { OrderSetupReview } from "../../../Common/Review/Component/OrderSetupReview";
import { hasDecisionMaker } from "../../Resource/Content";
import { InvestmentPathSelection } from "../../../../../../Shared/IST/Components/InvestmentPathSelection";
import { SwitchableHolding, SwitchSellAllocation, SwitchSellTableRow } from "../../Store/SwitchStoreStatic";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { SwitchOrderSetupReview } from "../../../Common/Review/Component/SwitchOrderSetupReview";

interface ISwitchFromInvestmentProps extends IProcessComponentProps {
    type: SubAccountType;
}

interface ISwitchFromInvestmentState {
    holdings: SwitchableHolding[];
    sellAllocations: SwitchSellAllocation[];
    totalValue: number;
    orderType: OrderType;
    isLoading: boolean;
    userInfo: User;
    treasuredAssetProductIds: Array<number>;
    isLoadingHoldings: boolean;
}

export class SwitchFromInvestment extends ProcessComponent<ISwitchFromInvestmentProps, ISwitchFromInvestmentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            holdings: [],
            sellAllocations: [],
            totalValue: 0,
            orderType: OrderType.Value,
            isLoading: false,
            isLoadingHoldings: true,
            userInfo: UserStore.getUserInfo(),
            treasuredAssetProductIds: null,
        };
    }

    private getTreasuredAssets() {
        const _treasuredAssetProductIds = TreasuredAssetsStore.getTreasuredAssetsProductIds();

        if (!_treasuredAssetProductIds) {
            const params = getQueryParameters();

            Action.getSubAccountTreasuredAssetsProductIds(params["subaccounthierarchyid"], (data: Array<number>) => {
                this.setState({ treasuredAssetProductIds: data });
            });
        } else {
            this.setState({ treasuredAssetProductIds: _treasuredAssetProductIds });
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.getTreasuredAssets();
        InvestmentSelectionAction.setIsUnderTransfer(true);
        this.loadHoldings();
    }

    private loadHoldings() {
        const params = getQueryParameters();
        const subAccountType = this.props.type;

        this.setState({ isLoadingHoldings: true });

        BuyAction.getReuseHoldings(
            params["hierarchyid"],
            params["subaccounthierarchyid"],
            0,
            (data: ClientTradableInvestments) => {
                const individual = data.CurrentIndividualHoldings.filter(
                    (ele) => ele.DisplayName != "GBP Cash" && ele.CurrQuantity > 0
                );
                const model = data.CurrentModelHoldings.filter((ele) => ele.CurrQuantity > 0);
                let ids = individual.map((ele) => ele.ProductId);
                ids = ids.concat(model.map((ele) => ele.ProductId));
                const query: InstrumentSearchQuery = {
                    ProductWrapperTypes: [this.props.type],
                    ProductIds: ids,
                    CanBeSold: true,
                    ResultsPerPage: ids.length,
                    ServiceProposition: data.AccountServiceProposition,
                };

                InvestmentSelectionAction.searchInstruments(
                    query,
                    params["subaccounthierarchyid"],
                    (result: Instrument[]) => {
                        let currentAssets: SwitchableHolding[] = [];

                        result.forEach((ele) => {
                            const obj = data.CurrentIndividualHoldings.find((item) => item.ProductId == ele.ProductId);
                            const model = data.CurrentModelHoldings.find((item) => item.ProductId == ele.ProductId);
                            if (obj) {
                                currentAssets.push({
                                    ProductId: obj.ProductId,
                                    ProductCode: obj.InstrumentCode,
                                    Name: obj.DisplayName,
                                    ProductType: obj.Type,
                                    MarketValue: obj.MarketValue,
                                    CurrQuantity: obj.CurrQuantity || 0,
                                    OriginQuantity: obj.OriginQuantity || obj.CurrQuantity || 0,
                                    SellOrders: obj.SellOrders || 0,
                                    UnitPrice: obj.UnitPrice,
                                    IsDeferred: ele?.IsDeferred || false,
                                    HasOpenValueSellOrder: obj.HasOpenValueSellOrder || false,
                                    FactsheetUrl: obj.FinexFactSheetUrl
                                });
                            } else if (model) {
                                currentAssets.push({
                                    ProductId: model.ProductId,
                                    ProductCode: model.InstrumentCode,
                                    Name: model.DisplayName,
                                    ProductType: model.Type,
                                    MarketValue: model.MarketValue,
                                    CurrQuantity: model.CurrQuantity || 0,
                                    OriginQuantity: model.OriginQuantity || model.CurrQuantity || 0,
                                    SellOrders: model.SellOrders || 0,
                                    UnitPrice: model.UnitPrice,
                                    IsDeferred: ele?.IsDeferred || false,
                                    HasOpenValueSellOrder: model.HasOpenValueSellOrder || false,
                                    FactsheetUrl: model.FinexFactSheetUrl
                                });
                            }

                        });

                        if (subAccountType == SubAccountType.Npr) {
                            currentAssets = currentAssets.filter((asset) => asset.ProductType != ProductType.OffPlatform);
                        }

                        const existingAllocations = this.props.data?.Contribution?.SwitchInstructionModel?.SellAllocations;
                        const totalValue = existingAllocations?.reduce(
                            (sum, alloc) => sum + (alloc.Value?.Amount || 0),
                            0
                        ) || 0;

                        if (currentAssets.length == 0) {
                            this.props.showErrorMsgPopUp(
                                ["There are no holdings in this product available to sell"],
                                true
                            );
                        } else {
                            this.setState({
                                holdings: currentAssets,
                                totalValue: parseFloat(totalValue.toFixed(2)),
                                isLoadingHoldings: false
                            });
                        }
                    }
                );
            }
        );
    }

    componentWillUnmount() {
        InvestmentSelectionAction.setIsUnderTransfer(false);
    }

    // TODO: TBC - show underlying allocations for model
    private onUnderlyingToggle(isToggle, index, row) {
        const _data = this.props.data.SellInvestmentList.concat();
        if (isToggle) {
            const item = _data[index];
            _data[index].isToggle = isToggle;
            if (item.underlying) {
                this.props.onChange({ SellInvestmentList: _data });
            } else {
                if (row.modelPortfolioType) {
                    InvestmentSelectionAction.getUnderlying(
                        row.Instrument.ProductId,
                        ModelPortfolioTabType.HubModel,
                        (data) => {
                            _data[index].underlying = data;
                            this.props.onChange({ SellInvestmentList: _data });
                        }
                    );
                } else {
                    InvestmentsAction.getInvestmentsModelSingle(
                        getQueryParameters()["subaccounthierarchyid"],
                        row.Instrument.InstrumentCode,
                        JsonDateParser.toJsonDateNoOffset(new Date()),
                        AssetClassType.Tier1,
                        (result) => {
                            _data[index].underlying = { ModelPortfolioProducts: [] };
                            result.forEach((ele) => {
                                ele.Investments.forEach((item) => {
                                    _data[index].underlying.ModelPortfolioProducts.push({
                                        Name: item.DisplayName,
                                        ProductCode: item.InstrumentCode,
                                        ...item,
                                        targetAllocation: (item.Allocation * 100).toFixed(2) + "%",
                                    });
                                });
                            });
                            this.props.onChange({ SellInvestmentList: _data });
                        }
                    );
                }
            }
        } else {
            _data[index].isToggle = isToggle;
            this.props.onChange({ SellInvestmentList: _data });
        }
    }

    isValid() {
        if (RestrictionStore.buyDisabled() || RestrictionStore.sellDisabled()) {
            return false;
        }
        const hasNoInvestment = this.state.holdings.length == 0;

        if (hasNoInvestment) {
            this.props.showErrorMsgPopUp(["There are no holdings in this product available to sell"]);
        }
        const over95PercentValueSells: string[] = this.state.sellAllocations
            .filter(alloc => {
                const holding = this.state.holdings.find(h => h.ProductId === alloc?.ProductId);
                return holding && alloc.OrderValue >= 0.95 * holding.MarketValue.Amount && alloc?.OrderQuantity == null;
            })
            .map(alloc => alloc.ProductCode);

        if (over95PercentValueSells.length > 0) {
            this.props.showErrorMsgPopUp([
                "Order Value cannot be greater than or equal to 95% of market value - " +
                over95PercentValueSells.join(", "),
            ]);
        }

        return super.isValid() && !hasNoInvestment && over95PercentValueSells.length === 0;
    }

    public render(): JSX.Element {
        const { data, type, onChange } = this.props;
        const params = getQueryParameters();

        const currentAllocations = data?.Contribution?.SwitchInstructionModel?.SellAllocations || [];
        const investmentsWithOpenSellOrder = this.state.holdings?.filter(ele => ele.HasOpenValueSellOrder) || [];
        const investmentsDeferred = this.state.holdings?.filter(ele => ele.IsDeferred) || [];
        const clientAccount = ClientAccountStore.getClientAccountInfo();

        const selectedInvestmentProductIds = currentAllocations.map(ele => ele.ProductId);
        const selectedTreasuredProductIds =
            this.state.treasuredAssetProductIds?.length > 0
                ? selectedInvestmentProductIds.filter(id => this.state.treasuredAssetProductIds.includes(id))
                : [];

        const restrictedMessage =
            this.state.userInfo?.IsAdviser
                ? GENERIC_RESTRICTIONS_MESSAGE
                : "An account with suspended or restricted activities cannot setup switch from investments";
        const treasuredAssetsMessage =
            "As you have selected a Treasured asset, a sell instruction will be created on this occasion. The instrument will continue to remain protected from auto-disinvestment, unless this a full sell order.";

        const tableData: SwitchSellTableRow[] = this.state.holdings.map(holding => {
            const allocation = currentAllocations.find(
                alloc => alloc.ProductId === holding.ProductId
            );

            return {
                Instrument: {
                    ProductId: holding.ProductId,
                    DisplayName: holding.Name,
                    Name: holding.Name,
                    Type: holding.ProductType,
                    MarketValue: holding.MarketValue,
                    InstrumentCode: holding.ProductCode,
                    CurrQuantity: holding.CurrQuantity,
                    OriginQuantity: holding.OriginQuantity,
                    SellOrders: holding.SellOrders,
                    UnitPrice: holding.UnitPrice,
                    HasOpenValueSellOrder: holding.HasOpenValueSellOrder,
                    IsDeferred: holding.IsDeferred
                },
                OrderValue: allocation?.Value?.Amount || 0,
                OrderPercentage: allocation?.Percentage,
                OrderQuantity: allocation?.Quantity,
                isSellAll: allocation?.Percentage && allocation?.Percentage === 100 ? true : false,
                Type: this.state.orderType,
                InstrumentCode: holding.ProductCode,
                tradeable: !holding.HasOpenValueSellOrder && holding.CurrQuantity > 0
            };
        });

        return (
            <div className="ist-wrap">
                <h2 className="mb-18">
                    <span>Switch from investments</span>
                </h2>
                {(RestrictionStore.buyDisabled() || RestrictionStore.sellDisabled()) && (
                    <Alert alertType={AlertType.Error} message={restrictedMessage} />
                )}
                <SwitchOrderSetupReview data={data} subAccountType={type} process={ProcessType.Sell} colNum={1} />
                <MoneyTextBoxEditor
                    label="Total switch from"
                    cssWrapper="control-size-245"
                    value={this.state.totalValue}
                    currency={CurrencyCode.GBP}
                    onBlur={(v) => {
                        if (!v) return;

                        const totalMarketValue = this.state.holdings.reduce(
                            (sum, curr) => sum + (curr.HasOpenValueSellOrder ? 0 : curr.MarketValue.Amount),
                            0
                        );

                        const newSellAllocations: InvestmentAllocation[] = this.state.holdings.map(holding => ({
                            ProductId: holding.ProductId,
                            Name: holding.Name,
                            Type: holding.ProductType,
                            Value: { Amount: Number(((holding.MarketValue.Amount / totalMarketValue) * v).toFixed(2)), Currency: CurrencyCode.GBP },
                            Percentage: null,
                            Quantity: null
                        }));

                        this.setState({
                            totalValue: v,
                            orderType: OrderType.Value,
                        });

                        this.props.onChange({
                            Contribution: {
                                ...this.props.data.Contribution,
                                SwitchInstructionModel: {
                                    ...this.props.data.Contribution.SwitchInstructionModel,
                                    SellAllocations: newSellAllocations
                                }
                            }
                        });
                    }}
                    onChange={(v) => {
                        this.setState({ totalValue: v ? Number(v.toFixed(2)) : v });
                    }}
                    maxNumberOfDecimalPlaces={{ errorMessage: "only allow two decimal places", value: 2 }}
                    moreThan={{ value: 0, errorMessage: "Value must larger than 0" }}
                    required="Please enter"
                />
                <hr />
                {investmentsWithOpenSellOrder.length > 0 && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={`Instrument ${investmentsWithOpenSellOrder.map(ele => ele.ProductCode).join(", ")} currently has an open value sell order. No further sell instructions can be placed until this has been confirmed.`}
                    />
                )}
                {investmentsDeferred.length > 0 && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={`The following Insured fund ${investmentsDeferred.map(ele => ele.ProductCode).join(", ")} is marked as deferred, and so a 'Switch from' order cannot be placed on this asset. If you wish to sell the asset, you will have to do so through the sell wizard in order to sell this down separately.`}
                    />
                )}
                <div className="row mt-5">
                    <div className="col-6"></div>
                    <div className="col-6 text-right"></div>
                </div>
                {selectedTreasuredProductIds.length > 0 && (
                    <Alert alertType={AlertType.Warning} message={treasuredAssetsMessage} />
                )}
                <PartLoading isLoading={this.state.isLoading}>
                    <BuySellAllocationTable
                        excludeLimit
                        totalValue={this.state.totalValue}
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                        onToggle={(isToggle, index, rowData) => this.onUnderlyingToggle(isToggle, index, rowData)}
                        data={tableData}
                        onChange={(list) => {
                            const newSellAllocations: SwitchSellAllocation[] = list.map(item => {

                                const holding = this.state.holdings.find(h => h.ProductId === item.Instrument.ProductId);
                                if (item.isSellAll) {
                                    const orderValue = holding.MarketValue.Amount;
        
                                    return {
                                        ...holding,
                                        OrderValue: orderValue,
                                        OrderPercentage: item.OrderPercentage,
                                        OrderQuantity: item.OrderQuantity,
                                        DisinvestAll: false
                                    };
                                }
                                if (item.OrderQuantity) {
                                    const unitPrice = holding.UnitPrice?.Amount || 0;
                                    const orderValue = Math.min(Number((item.OrderQuantity * unitPrice).toFixed(2)), holding.MarketValue.Amount);
                                    const orderPercentage = Number((orderValue / holding.MarketValue.Amount * 100).toFixed(2));
                                    return {
                                        ...holding,
                                        OrderValue: orderValue,
                                        OrderPercentage: orderPercentage,
                                        OrderQuantity: item.OrderQuantity,
                                        DisinvestAll: false
                                    };
                                }
                                if (item.OrderPercentage) {
                                    const holdingValue = holding.MarketValue.Amount;
                                    const orderValue = Number((holdingValue * (item.OrderPercentage / 100)).toFixed(2));
                                    const orderQuantity = holding.UnitPrice?.Amount
                                        ? Number((orderValue / holding.UnitPrice.Amount).toFixed(4))
                                        : null;
                                    return {
                                        ...holding,
                                        OrderValue: orderValue,
                                        OrderPercentage: item.OrderPercentage,
                                        OrderQuantity: orderQuantity,
                                        DisinvestAll: false
                                    };
                                }
                                if (item.OrderValue) {
                                    return {
                                        ...holding,
                                        OrderValue: item.OrderValue,
                                        OrderPercentage: null,
                                        OrderQuantity: null,
                                        DisinvestAll: false
                                    };
                                }

                                return (
                                    this.state.sellAllocations.find(a => a?.ProductId === item.Instrument.ProductId) && {
                                        ...holding,
                                        OrderValue: 0,
                                        OrderPercentage: null,
                                        OrderQuantity: null
                                    }
                                );
                            });


                            var newTotal = newSellAllocations.filter(Boolean).reduce((sum, { OrderValue }) => sum + (OrderValue ?? 0), 0);


                            this.setState({
                                totalValue: isNaN(newTotal) ? 0 : Number(newTotal.toFixed(2)),
                                sellAllocations: newSellAllocations
                            });

                            this.props.onChange({
                                Contribution: {
                                    ...this.props.data.Contribution,
                                    SwitchInstructionModel: {
                                        ...this.props.data.Contribution.SwitchInstructionModel,
                                        SellAllocations: newSellAllocations.filter(allocation => allocation?.ProductId).map(allocation => ({
                                            ProductId: allocation.ProductId,
                                            Value: { Amount: allocation.OrderValue, Currency: CurrencyCode.GBP },
                                            Percentage: allocation.OrderPercentage,
                                            Quantity: allocation.OrderQuantity,
                                            Name: allocation.Name,
                                            Type: allocation.ProductType
                                        }))
                                    }
                                }
                            });
                        }}
                        orderType={OrderType.Value}
                        process={ProcessType.SwitchFrom}
                        onRef={(ref) => this.appendChild(ref)}
                        onTreasuredAssetChange={() => { }}
                        ChecktreasuredAsset={this.state.treasuredAssetProductIds}
                        depositProducts={[]}
                        batchType={data.BatchType}
                    />
                    {hasDecisionMaker(data.SellInvestmentList) && (
                        <DecisionMaker
                            onRef={(ref) => this.appendChild(ref)}
                            data={data}
                            onChange={onChange}
                            clAccountId={params["claccountid"]}
                            headAccountHierarchyId={clientAccount?.HierarchyId}
                            companyCode={clientAccount?.Company}
                            accountAdviceType={
                                clientAccount?.Discretionary
                                    ? AccountAdviceType.Discretionary
                                    : AccountAdviceType.NonDiscretionary
                            }
                            showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                        />
                    )}
                </PartLoading>
            </div>
        );
    }
}
